import { useEffect, useState } from 'react';
import {
  cloneSchemaDocument,
  createCategory,
  parseNullableInteger,
  slugify,
} from './schema-editor-helpers';
import type { SchemaEditorCommonProps } from './schema-editor-types';
import { SchemaEditorActions, SchemaEmptySelection } from './SchemaEditorActions';

export function SchemaCategoriesEditor(props: SchemaEditorCommonProps): React.JSX.Element {
  const [selectedId, setSelectedId] = useState(props.document.categories[0]?.id ?? '');
  const [search, setSearch] = useState('');

  useEffect(syncSelection, [props.document.categories, selectedId]);

  const selected = findSelectedCategory();
  const rows: React.JSX.Element[] = [];
  const normalizedSearch = search.trim().toLowerCase();
  for (const category of props.document.categories) {
    if (normalizedSearch !== '' && !category.displayName.toLowerCase().includes(normalizedSearch) && !category.key.includes(normalizedSearch)) {
      continue;
    }
    rows.push(
      <button
        className={category.id === selectedId ? 'schema-list-row active' : 'schema-list-row'}
        data-id={category.id}
        key={category.id}
        onClick={selectCategory}
        type="button"
      >
        <span><strong>{category.displayName || 'Untitled category'}</strong><small>{category.key || 'Missing key'}</small></span>
        <code>{category.wireCode ?? '—'}</code>
      </button>,
    );
  }

  return (
    <section className="schema-editor-grid">
      <aside className="surface schema-definition-browser">
        <div className="surface-heading"><div><span className="eyebrow">Schema</span><h2>Categories</h2></div><strong>{props.document.categories.length}</strong></div>
        <input className="schema-search" placeholder="Search categories" value={search} onChange={changeSearch} />
        {props.canManage && <button className="secondary-button schema-add-button" onClick={addCategory} type="button">Add category</button>}
        <div className="schema-list">{rows.length === 0 ? <p className="schema-empty-list">No categories found.</p> : rows}</div>
      </aside>

      <article className="surface schema-detail-editor">
        {selected === undefined ? <SchemaEmptySelection label="category" /> : (
          <>
            <div className="surface-heading">
              <div><span className="eyebrow">Category definition</span><h2>{selected.displayName || 'Untitled category'}</h2></div>
              <code>{selected.id.slice(0, 8)}</code>
            </div>
            <div className="schema-form-grid">
              <label>Display name<input name="displayName" value={selected.displayName} onChange={changeField} disabled={!props.canManage} /></label>
              <label>Technical key<input name="key" value={selected.key} onChange={changeField} disabled={!props.canManage} spellCheck={false} /></label>
              <label>Wire code<input name="wireCode" type="number" min="1" max="65535" value={selected.wireCode ?? ''} onChange={changeField} disabled={!props.canManage} /></label>
              <label className="schema-wide-field">Description<textarea name="description" value={selected.description} onChange={changeField} disabled={!props.canManage} /></label>
            </div>
            {props.canManage && <SchemaEditorActions busy={props.busy} dirty={props.dirty} onDelete={deleteCategory} onReset={props.onReset} onSave={save} />}
          </>
        )}
      </article>
    </section>
  );

  function syncSelection(): void {
    if (selectedId !== '' && findCategoryIndex(props, selectedId) >= 0) {
      return;
    }
    setSelectedId(props.document.categories[0]?.id ?? '');
  }

  function findSelectedCategory() {
    const index = findCategoryIndex(props, selectedId);
    return index < 0 ? undefined : props.document.categories[index];
  }

  function changeSearch(event: React.ChangeEvent<HTMLInputElement>): void {
    setSearch(event.target.value);
  }

  function selectCategory(event: React.MouseEvent<HTMLButtonElement>): void {
    setSelectedId(event.currentTarget.dataset.id ?? '');
  }

  function addCategory(): void {
    const document = cloneSchemaDocument(props.document);
    const category = createCategory(document);
    document.categories.push(category);
    props.onDocumentChange(document);
    setSelectedId(category.id);
  }

  function changeField(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void {
    const document = cloneSchemaDocument(props.document);
    const index = findCategoryIndex({ document }, selectedId);
    if (index < 0) {
      return;
    }
    const category = document.categories[index]!;
    const field = event.target.name;
    if (field === 'wireCode') {
      category.wireCode = parseNullableInteger(event.target.value);
    } else if (field === 'displayName') {
      const priorSuggestedKey = slugify(category.displayName);
      category.displayName = event.target.value;
      if (category.key === priorSuggestedKey || category.key === '') {
        category.key = slugify(category.displayName);
      }
    } else if (field === 'key') {
      category.key = event.target.value;
    } else if (field === 'description') {
      category.description = event.target.value;
    }
    props.onDocumentChange(document);
  }

  function deleteCategory(): void {
    if (!window.confirm('Delete this category from the draft? Referencing events will remain and must be repaired before publishing.')) {
      return;
    }
    const document = cloneSchemaDocument(props.document);
    const index = findCategoryIndex({ document }, selectedId);
    if (index < 0) {
      return;
    }
    document.categories.splice(index, 1);
    props.onDocumentChange(document);
    setSelectedId(document.categories[index]?.id ?? document.categories[index - 1]?.id ?? '');
  }

  async function save(): Promise<void> {
    await props.onSave();
  }
}

function findCategoryIndex(props: Pick<SchemaEditorCommonProps, 'document'>, id: string): number {
  for (let index = 0; index < props.document.categories.length; index += 1) {
    if (props.document.categories[index]!.id === id) {
      return index;
    }
  }
  return -1;
}
